import {
  CampaignStatus,
  useCampaignFilter,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import { Colors, Icon, tokens, InfiniteDataGrid } from '@gripp/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { CampaignsFilter } from '../hooks/useCampaigns';

const STATUS_ICON: Record<
  CampaignStatus,
  { name: 'Edit' | 'Calendar' | 'Circle Check'; color: string; label: string }
> = {
  [CampaignStatus.Draft]: {
    name: 'Edit',
    color: Colors.role.textSecondary,
    label: 'Draft',
  },
  [CampaignStatus.Scheduled]: {
    name: 'Calendar',
    color: tokens.colors.semantic.info,
    label: 'Scheduled',
  },
  [CampaignStatus.Sent]: {
    name: 'Circle Check',
    color: tokens.colors.semantic.success,
    label: 'Sent',
  },
};

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const useCampaignColumns = (): ColumnDef<any, any>[] => {
  const { t } = useTranslation();

  // InfiniteDataGrid renders cells through react-native-paper's DataTable.Cell,
  // which wraps non-element children in its own <Text> that inherits the
  // browser's default serif font (Times New Roman on web). Every text cell
  // must render via <CellText> (or its own styled Text) to land on the Crop
  // Circle font.
  return [
    {
      accessorKey: 'title',
      header: t('campaigns.columns.name', 'Name'),
      meta: { sortable: true },
      cell: ({ getValue }: { getValue: () => string }) => (
        <CellText>{getValue() ?? '-'}</CellText>
      ),
    },
    {
      accessorKey: 'status',
      header: t('campaigns.columns.status', 'Status'),
      cell: ({ getValue }: { getValue: () => CampaignStatus }) => {
        const status = getValue();
        const def = status ? STATUS_ICON[status] : undefined;
        if (!def) return <CellText>-</CellText>;
        const label = t(
          `campaigns.status.${status.toLowerCase()}`,
          def.label
        );
        // The status cell renders as `<div>` on web (RN Web's View). The
        // native HTML `title` attribute on the host gives the hover tooltip
        // a reviewer asked for; accessibilityLabel covers screen readers.
        return (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <View accessibilityLabel={label} {...({ title: label } as any)}>
            <Icon name={def.name} size={20} color={def.color} />
          </View>
        );
      },
    },
    {
      accessorKey: 'replyCount',
      header: t('campaigns.columns.responses', 'Responses'),
      cell: ({ getValue }: { getValue: () => number | undefined }) => {
        const n = getValue() ?? 0;
        return (
          <View style={styles.responsesCell}>
            <Icon
              name="Comment"
              size={16}
              color={Colors.role.textSecondary}
            />
            <CellText>{n}</CellText>
          </View>
        );
      },
    },
    {
      accessorKey: 'groupIds',
      header: t('campaigns.columns.groups', 'Groups'),
      cell: ({ getValue }: { getValue: () => string[] }) => (
        <CellText>{getValue()?.length ?? 0}</CellText>
      ),
    },
    {
      accessorKey: 'updatedAt',
      header: t('campaigns.columns.lastUpdated', 'Last Updated'),
      meta: { sortable: true },
      cell: ({ getValue }: { getValue: () => string }) => {
        const val = getValue();
        return (
          <CellText>
            {val ? new Date(val).toLocaleDateString() : '-'}
          </CellText>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('campaigns.columns.createdAt', 'Created at'),
      meta: { sortable: true },
      cell: ({ getValue }: { getValue: () => string }) => {
        const val = getValue();
        return (
          <CellText>
            {val ? new Date(val).toLocaleDateString() : '-'}
          </CellText>
        );
      },
    },
  ];
};

// Shared cell-text component so every column lands on the Crop Circle font.
const CellText = ({ children }: { children: React.ReactNode }) => (
  <Text style={styles.cellText}>{children}</Text>
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CampaignsListProps {
  filter?: Partial<Omit<CampaignsFilter, 'workspaceId'>>;
  onRowClick?: (row: any) => void;
  onItemsCountChange?: (count: number) => void;
}

// ---------------------------------------------------------------------------
// Main list component
// ---------------------------------------------------------------------------

export const CampaignsList = ({
  filter,
  onRowClick,
  onItemsCountChange,
}: CampaignsListProps) => {
  const workspace = useCurrentWorkspace();
  const columns = useCampaignColumns();
  const { data: campaignFilter } = useCampaignFilter();

  const { t } = useTranslation();

  if (!workspace?.id) return null;

  // Persisted filter from the toolbar (keyword, status) merges over any
  // hard-coded `filter` prop the caller passes. The caller's workspaceId
  // override would be wrong anyway — list is always scoped to current ws.
  const campaignsFilter: CampaignsFilter = {
    workspaceId: workspace.id,
    ...filter,
    ...(campaignFilter?.keyword ? { keyword: campaignFilter.keyword } : {}),
    ...(campaignFilter?.status ? { status: campaignFilter.status } : {}),
  };

  return (
    <View style={styles.container}>
      <InfiniteDataGrid
        query={LIST_CAMPAIGNS}
        dataPrefix="campaign.listCampaigns"
        modelName="campaign"
        columns={columns}
        filter={campaignsFilter}
        pageSize={50}
        hideHeader={false}
        showScrollIndicator={true}
        onRowClick={onRowClick}
        onItemsCountChange={onItemsCountChange}
        rowTestIdPrefix="campaign"
        textEmpty={t('campaigns.list.empty', 'No campaigns found')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  responsesCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  cellText: {
    ...tokens.typography.body.medium,
    color: Colors.role.textStrong,
    // The row is a Pressable (pointer cursor), but RN-Web <Text> defaults to a
    // text/I-beam cursor and overrides it — keep the whole row pointer.
    cursor: 'pointer',
  },
});

// ---------------------------------------------------------------------------
// GraphQL query — colocated for legibility (same pattern as routinesList.tsx)
// ---------------------------------------------------------------------------

const LIST_CAMPAIGNS = `
  query ListCampaigns($filter: CampaignsFilter!, $page: PageRequest, $orderBy: OrderByInput) {
    campaign {
      listCampaigns(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          title
          description
          status
          groupIds
          targetedAssetIds
          scheduledSendAt
          sentAt
          createdAt
          updatedAt
          replyCount
        }
      }
    }
  }
`;
